import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Calendar,
  CheckCircle,
  Clock,
  Users,
  ArrowDown,
  Play,
  HelpCircle,
} from "lucide-react";
import {
  getPublicEventLandingPage,
  submitEventRegistration,
} from "../../services/eventLandingServices";

/* ----------------------------- helpers ------------------------------- */

const formatDate = (d) => {
  if (!d) return "";
  try {
    const date = new Date(d);
    const datePart = date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const timePart = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${datePart} · ${timePart}`;
  } catch {
    return "";
  }
};

const useCountdown = (targetIso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => {
    if (!targetIso) return null;
    const target = new Date(targetIso).getTime();
    const diff = Math.max(0, target - now);
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);
    return { days, hours, minutes, seconds, isLive: diff === 0 };
  }, [targetIso, now]);
};

const buildGoogleCalendarUrl = (event) => {
  const title = event.heroHeadline || event.yourName || "Event";
  const description = event.heroSubheadline || event.description || "";
  const start = event.scheduledStartTime;
  const end = event.scheduledEndTime;

  if (!start) return null;

  // Format as YYYYMMDDTHHmmssZ
  const fmt = (d) =>
    new Date(d)
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}/, "");

  const startStr = fmt(start);
  // Default to 1 hour if no end time
  const endStr = end
    ? fmt(end)
    : fmt(new Date(new Date(start).getTime() + 60 * 60 * 1000));

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${startStr}/${endStr}`,
    details: description,
  });

  if (event.timezone) {
    params.set("ctz", event.timezone);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const scrollToForm = () => {
  document
    .getElementById("registration-form")
    ?.scrollIntoView({ behavior: "smooth" });
};

/* ---------------------------- subcomponents -------------------------- */

const CountdownBar = ({ event }) => {
  const cd = useCountdown(event.scheduledStartTime);
  const showCountdown = event.showCountdownTimer && cd && !cd.isLive;
  const showScarcity =
    event.showLimitedSeatsWarning && event.remainingSpots != null;

  if (!showCountdown && !showScarcity) return null;

  const Box = ({ value, label }) => (
    <div className="flex flex-col items-center px-2 sm:px-3">
      <span className="text-2xl md:text-3xl font-bold tabular-nums">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-[10px] uppercase tracking-wider opacity-70">
        {label}
      </span>
    </div>
  );

  return (
    <div className="bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white py-3">
      <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
        {showCountdown && (
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5" />
            <span className="font-medium hidden sm:inline">Starts in</span>
            <div className="flex items-center divide-x divide-white/30">
              <Box value={cd.days} label="Days" />
              <Box value={cd.hours} label="Hrs" />
              <Box value={cd.minutes} label="Min" />
              <Box value={cd.seconds} label="Sec" />
            </div>
          </div>
        )}
        {showScarcity && (
          <div className="flex items-center gap-2 font-semibold">
            <Users className="h-5 w-5" />
            Only {event.remainingSpots} of {event.totalCapacity} spots left
          </div>
        )}
      </div>
    </div>
  );
};

const RegistrationForm = ({
  event,
  onSubmit,
  submitting,
  submitted,
  className = "",
  dark = false,
}) => {
  const [data, setData] = useState({ name: "", email: "", company: "" });
  const handle = (e) => {
    e.preventDefault();
    onSubmit(data);
  };

  const baseCard = dark
    ? "bg-white/10 backdrop-blur-lg text-white border border-white/15"
    : "bg-white text-gray-900";

  if (submitted) {
    const calUrl = buildGoogleCalendarUrl(event);
    return (
      <div
        id="registration-form"
        className={`${baseCard} rounded-2xl shadow-xl p-8 text-center ${className}`}
      >
        <CheckCircle className="h-14 w-14 text-green-400 mx-auto mb-4" />
        <h3 className="text-2xl font-bold mb-2">You're registered!</h3>
        <p className={dark ? "text-white/80" : "text-gray-600"}>
          We've saved your spot. Check your email for details.
        </p>
        {calUrl && (
          <a
            href={calUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-xl bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white font-semibold hover:shadow-lg transition-all"
          >
            <Calendar className="h-5 w-5" />
            Add to Calendar
          </a>
        )}
      </div>
    );
  }

  const inputCls = dark
    ? "w-full px-4 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-[#00c853] focus:ring-2 focus:ring-[#00c853]/20"
    : "w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-[#6a1fbf] focus:outline-none focus:ring-2 focus:ring-[#6a1fbf]/20 text-gray-900";
  const labelCls = dark
    ? "block text-sm font-medium text-white/90 mb-1"
    : "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div
      id="registration-form"
      className={`${baseCard} rounded-2xl shadow-xl p-6 md:p-8 ${className}`}
    >
      <h3 className="text-xl md:text-2xl font-bold mb-1">
        {event.registrationTitle || "Save Your Spot"}
      </h3>
      <p
        className={`text-sm mb-5 ${dark ? "text-white/70" : "text-gray-500"}`}
      >
        Reserve your free seat below.
      </p>
      <form onSubmit={handle} className="space-y-4">
        <div>
          <label className={labelCls}>Full name *</label>
          <input
            required
            type="text"
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            className={inputCls}
            placeholder="Jane Doe"
          />
        </div>
        <div>
          <label className={labelCls}>Email address *</label>
          <input
            required
            type="email"
            value={data.email}
            onChange={(e) => setData({ ...data, email: e.target.value })}
            className={inputCls}
            placeholder="jane@example.com"
          />
        </div>
        <div>
          <label className={labelCls}>Company</label>
          <input
            type="text"
            value={data.company}
            onChange={(e) => setData({ ...data, company: e.target.value })}
            className={inputCls}
            placeholder="Acme Inc."
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white font-semibold hover:shadow-lg transition-all disabled:opacity-60"
        >
          {submitting ? "Reserving..." : event.ctaButtonText || "Register Now"}
        </button>
        {event.showLimitedSeatsWarning && event.remainingSpots != null && (
          <p
            className={`text-xs text-center flex items-center justify-center gap-1 ${
              dark ? "text-orange-300" : "text-orange-600"
            }`}
          >
            <Users className="h-3.5 w-3.5" />
            Only {event.remainingSpots} of {event.totalCapacity} spots left
          </p>
        )}
      </form>
    </div>
  );
};

const HeroDateBadge = ({ date, theme = "light" }) => (
  <div
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${
      theme === "dark"
        ? "bg-white/10 text-white"
        : "bg-white text-gray-700 shadow"
    }`}
  >
    <Calendar
      className={`h-5 w-5 ${theme === "dark" ? "text-[#00c853]" : "text-[#6a1fbf]"}`}
    />
    <span className="font-medium">{formatDate(date)}</span>
  </div>
);

const SpeakersGrid = ({ speakers, theme = "light" }) => {
  if (!speakers?.length) return null;
  const isDark = theme === "dark";
  return (
    <div
      className={`grid md:grid-cols-2 ${
        speakers.length > 2 ? "lg:grid-cols-3" : ""
      } gap-6`}
    >
      {speakers.map((s, i) => (
        <div
          key={i}
          className={`text-center p-5 rounded-2xl ${
            isDark
              ? "bg-white/5 border border-white/10"
              : "bg-white shadow-sm border border-gray-100"
          }`}
        >
          {s.photoUrl || s.photo_url ? (
            <img
              src={s.photoUrl || s.photo_url}
              alt={s.name}
              className="w-24 h-24 rounded-full mx-auto mb-3 object-cover"
            />
          ) : (
            <div
              className={`w-24 h-24 rounded-full mx-auto mb-3 ${
                isDark ? "bg-white/10" : "bg-gray-100"
              }`}
            />
          )}
          <h4
            className={`text-lg font-bold ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {s.name}
          </h4>
          {s.title && (
            <p className="text-[#6a1fbf] font-medium text-sm">{s.title}</p>
          )}
          {s.bio && (
            <p
              className={`text-sm mt-2 ${
                isDark ? "text-white/70" : "text-gray-600"
              }`}
            >
              {s.bio}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

const SocialProofSection = ({ items, theme = "light" }) => {
  if (!items?.length) return null;
  const isDark = theme === "dark";
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {items.map((p, i) => (
        <div
          key={i}
          className={`rounded-xl p-5 ${
            isDark
              ? "bg-white/5 border border-white/10"
              : "bg-gray-50 border border-gray-100"
          }`}
        >
          {p.type === "logo" && (p.logoUrl || p.logo_url) ? (
            <img
              src={p.logoUrl || p.logo_url}
              alt="Logo"
              className="h-10 mx-auto opacity-80"
            />
          ) : (
            <>
              <p
                className={`italic mb-2 ${
                  isDark ? "text-white/80" : "text-gray-700"
                }`}
              >
                "{p.content}"
              </p>
              {p.author && (
                <p
                  className={`font-semibold text-sm ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  — {p.author}
                </p>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
};

/* ------------------------------ layouts ------------------------------ */

// 1. CLASSIC SAAS — 2-column hero + bullets, sidebar form
const ClassicSaasLayout = ({ event, ...formProps }) => (
  <div className="min-h-screen bg-gray-50">
    <CountdownBar event={event} />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          {event.logoUrl && (
            <img src={event.logoUrl} alt="Logo" className="h-12 mb-6" />
          )}
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-5 leading-tight">
            {event.heroHeadline || event.yourName}
          </h1>
          {event.heroSubheadline && (
            <p className="text-lg md:text-xl text-gray-600 mb-7">
              {event.heroSubheadline}
            </p>
          )}
          {event.heroBullets?.length > 0 && (
            <ul className="space-y-3 mb-7">
              {event.heroBullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#00c853] flex-shrink-0 mt-0.5" />
                  <span className="text-base text-gray-700">{b}</span>
                </li>
              ))}
            </ul>
          )}
          <HeroDateBadge date={event.scheduledStartTime} />
        </div>
        <div>
          <RegistrationForm event={event} {...formProps} />
        </div>
      </div>
    </div>
  </div>
);

// 2. CENTERED BIG PROMISE — centered hero, scroll-to-form CTA
const CenteredBigPromiseLayout = ({ event, ...formProps }) => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
    <CountdownBar event={event} />
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center">
      {event.logoUrl && (
        <img src={event.logoUrl} alt="Logo" className="h-16 mx-auto mb-8" />
      )}
      <div className="inline-block bg-[#6a1fbf]/10 text-[#6a1fbf] px-4 py-2 rounded-full text-sm font-semibold mb-6">
        {event.format === "evergreen" ? "Free On-Demand Training" : "Free Live Training"}
      </div>
      <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
        {event.heroHeadline || event.yourName}
      </h1>
      {event.heroSubheadline && (
        <p className="text-xl lg:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto">
          {event.heroSubheadline}
        </p>
      )}
      <div className="flex justify-center mb-8">
        <HeroDateBadge date={event.scheduledStartTime} />
      </div>
      <button
        onClick={scrollToForm}
        className="inline-flex items-center gap-2 px-12 py-5 rounded-lg bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white text-lg font-semibold hover:shadow-2xl hover:shadow-purple-500/30 transition-all"
      >
        {event.ctaButtonText || "Register Now"}
        <ArrowDown className="w-5 h-5" />
      </button>
    </div>
    <div className="bg-white border-t border-gray-200 py-16">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
        <RegistrationForm event={event} {...formProps} />
      </div>
    </div>
  </div>
);

// 3. BACKGROUND CARD — full-bleed image bg, white card overlay, speakers + social proof
const BackgroundCardLayout = ({ event, ...formProps }) => {
  const bgStyle = event.heroImageUrl
    ? { backgroundImage: `url(${event.heroImageUrl})` }
    : {
        background:
          "linear-gradient(135deg, #001233 0%, #1a1052 50%, #00482b 100%)",
      };

  return (
    <div className="min-h-screen bg-gray-50">
      <CountdownBar event={event} />
      <div
        className="relative bg-cover bg-center py-16 lg:py-28"
        style={bgStyle}
      >
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
            {event.logoUrl && (
              <img src={event.logoUrl} alt="Logo" className="h-12 mb-6" />
            )}
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {event.heroHeadline || event.yourName}
            </h1>
            {event.heroSubheadline && (
              <p className="text-xl text-gray-600 mb-6">
                {event.heroSubheadline}
              </p>
            )}
            <div className="mb-8">
              <HeroDateBadge date={event.scheduledStartTime} />
            </div>
            {event.heroBullets?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  What You'll Get:
                </h3>
                <ul className="space-y-2.5">
                  {event.heroBullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#00c853] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <RegistrationForm
              event={event}
              {...formProps}
              className="!shadow-none !p-0"
            />
          </div>
        </div>
      </div>

      {(event.speakers?.length > 0 || event.socialProof?.length > 0) && (
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            {event.speakers?.length > 0 && (
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                  Your Speakers
                </h2>
                <SpeakersGrid speakers={event.speakers} />
              </div>
            )}
            {event.socialProof?.length > 0 && (
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                  Loved by professionals
                </h2>
                <SocialProofSection items={event.socialProof} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// 4. AGENDA FIRST — gradient hero, then 2-col agenda + speakers + sticky form
const AgendaFirstLayout = ({ event, ...formProps }) => (
  <div className="min-h-screen bg-gray-50">
    <CountdownBar event={event} />
    <div className="bg-gradient-to-br from-[#6a1fbf] to-[#00c853] text-white py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {event.logoUrl && (
          <img
            src={event.logoUrl}
            alt="Logo"
            className="h-14 mx-auto mb-6 brightness-0 invert"
          />
        )}
        <h1 className="text-4xl lg:text-5xl font-bold mb-6">
          {event.heroHeadline || event.yourName}
        </h1>
        {event.heroSubheadline && (
          <p className="text-xl lg:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
            {event.heroSubheadline}
          </p>
        )}
        <div className="flex items-center justify-center gap-2 text-white mb-8">
          <Calendar className="w-5 h-5" />
          <span className="text-lg font-medium">
            {formatDate(event.scheduledStartTime)}
          </span>
        </div>
        <button
          onClick={scrollToForm}
          className="inline-flex items-center gap-2 px-12 py-5 rounded-lg bg-white text-[#6a1fbf] hover:bg-gray-100 text-lg font-semibold transition-all"
        >
          {event.ctaButtonText || "Register Now"}
          <ArrowDown className="w-5 h-5" />
        </button>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What You'll Learn
          </h2>
          {event.agenda?.length > 0 ? (
            <ul className="space-y-4">
              {event.agenda.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm border border-gray-100"
                >
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#6a1fbf] to-[#00c853] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {i + 1}
                  </div>
                  <span className="text-base text-gray-700 pt-1.5">
                    {typeof item === "string" ? item : item.title}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-gray-600">
              Agenda details coming soon...
            </div>
          )}
        </div>

        <div className="space-y-8">
          {event.speakers?.length > 0 && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Meet Your Hosts
              </h2>
              <div className="space-y-4">
                {event.speakers.map((s, i) => (
                  <div
                    key={i}
                    className="flex gap-4 bg-white rounded-xl p-5 shadow-sm border border-gray-100"
                  >
                    {(s.photoUrl || s.photo_url) && (
                      <img
                        src={s.photoUrl || s.photo_url}
                        alt={s.name}
                        className="w-20 h-20 rounded-full object-cover flex-shrink-0"
                      />
                    )}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {s.name}
                      </h3>
                      {s.title && (
                        <p className="text-[#6a1fbf] font-medium mb-1">
                          {s.title}
                        </p>
                      )}
                      {s.bio && (
                        <p className="text-gray-600 text-sm">{s.bio}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="lg:sticky lg:top-8">
            <RegistrationForm event={event} {...formProps} />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// 5. EVERGREEN ON DEMAND — dark gradient, watch-now CTA, time slot picker, FAQ
const EvergreenOnDemandLayout = ({ event, ...formProps }) => {
  const [showForm, setShowForm] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const isPicker = event.evergreenCtaType === "time_slot_picker";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 text-white">
      <CountdownBar event={event} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="text-center mb-12">
          {event.logoUrl && (
            <img
              src={event.logoUrl}
              alt="Logo"
              className="h-14 mx-auto mb-6 brightness-0 invert"
            />
          )}
          <div className="inline-block bg-[#00c853] text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
            {isPicker ? "Choose Your Time" : "Watch Instantly"}
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
            {event.heroHeadline || event.yourName}
          </h1>
          {event.heroSubheadline && (
            <p className="text-xl lg:text-2xl text-white/80 mb-8 max-w-3xl mx-auto">
              {event.heroSubheadline}
            </p>
          )}
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-12 py-5 rounded-lg bg-gradient-to-r from-[#00c853] to-emerald-500 text-white text-lg font-semibold hover:shadow-2xl hover:shadow-emerald-500/30 transition-all"
            >
              <Play className="w-6 h-6" />
              {isPicker ? "Choose Your Time" : "Watch Now"}
            </button>
          )}
        </div>

        {showForm && (
          <div className="max-w-xl mx-auto">
            {isPicker && event.evergreenTimes?.length > 0 && (
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6 border border-white/15">
                <p className="text-white text-lg font-semibold mb-4">
                  Select Your Preferred Time:
                </p>
                <div className="grid gap-3">
                  {event.evergreenTimes.map((t, i) => {
                    const selected = selectedTime === t;
                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedTime(t)}
                        className={`flex items-center justify-between p-4 rounded-lg transition-all ${
                          selected
                            ? "bg-[#00c853] text-white"
                            : "bg-white/10 hover:bg-white/20 text-white"
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          <Clock className="w-5 h-5" />
                          <span className="font-medium">
                            {new Date(t).toLocaleString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </span>
                        </span>
                        {selected && <CheckCircle className="w-5 h-5" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <RegistrationForm event={event} {...formProps} dark />
          </div>
        )}
      </div>

      {event.socialProof?.length > 0 && (
        <div className="bg-white/5 backdrop-blur-lg py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-8">
              What Others Are Saying
            </h2>
            <SocialProofSection items={event.socialProof} theme="dark" />
          </div>
        </div>
      )}

      {event.faq?.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-center mb-8 flex items-center justify-center gap-2">
            <HelpCircle className="w-7 h-7" /> Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {event.faq.map((item, i) => (
              <div
                key={i}
                className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/15"
              >
                <h3 className="text-lg font-bold mb-2">{item.question}</h3>
                <p className="text-white/80">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const LAYOUT_MAP = {
  "classic-saas": ClassicSaasLayout,
  "centered-big-promise": CenteredBigPromiseLayout,
  "background-card": BackgroundCardLayout,
  "agenda-first": AgendaFirstLayout,
  "evergreen-on-demand": EvergreenOnDemandLayout,
};

/* ------------------------------- page -------------------------------- */

const EventLandingPublic = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getPublicEventLandingPage(slug)
      .then((res) => {
        if (mounted) setEvent(res.data);
      })
      .catch((err) => {
        if (mounted)
          setError(err?.response?.data?.message || "Event not found");
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [slug]);

  const handleRegister = async (formData) => {
    setSubmitting(true);
    try {
      const res = await submitEventRegistration(slug, formData);
      setSubmitted(true);
      // Reflect updated remaining spots locally
      if (event && typeof event.remainingSpots === "number") {
        setEvent({ ...event, remainingSpots: Math.max(0, event.remainingSpots - 1) });
      }
      return res;
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Registration failed. Please try again.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#001233] text-white">
        Loading event...
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#001233] text-white px-4">
        <h1 className="text-3xl font-bold mb-2">Event not found</h1>
        <p className="text-white/60">
          {error || "This event may have been removed."}
        </p>
      </div>
    );
  }

  const Layout = LAYOUT_MAP[event.layoutType] || ClassicSaasLayout;
  return (
    <Layout
      event={event}
      onSubmit={handleRegister}
      submitting={submitting}
      submitted={submitted}
    />
  );
};

export default EventLandingPublic;
